// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "../../../theme/SwiperOverrides.css";

import * as styles from "./ParagraphShareableCarousel.module.scss";

import React, { useState } from "react";
// import Swiper core and required modules
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { SocialMediaIcons } from "../../../components/common";
import { ArrowCircle } from "../../../components/common/Icons";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import { useClientSide } from "../../../hooks/useClientSide";
import { useIsTouchDevice } from "../../../hooks/useIsTouchDevice";
import shareDog from "../../../images/png/social_share_dog2.png";
import shareShape from "../../../static/img/sandyfur_shape.svg";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";

export interface ShareableImageProps {
	imagePageUrl: string;
	imageSrc: string;
	imageAlt: string;
}
export interface ParagraphShareableCarouselProps {
	shareableImages: ShareableImageProps[];
}

export const getParagraphShareableCarouselProps = (
	data: Queries.ParagraphShareableCarouselDataFragment,
): ParagraphShareableCarouselProps => ({
	shareableImages: data?.shareableImages.map((image) => ({
		imageSrc: image.imageSrc || "",
		imageAlt: image.imageAlt || "",
		imagePageUrl: image.imagePageUrl || "",
	})),
});
export const ParagraphShareableCarousel: React.FC<
	ParagraphShareableCarouselProps
> = ({ shareableImages }) => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const isClientSide = useClientSide();
	const touchDevice = useIsTouchDevice();
	const [swiper, setSwiper] = useState(null);

	const prevSlide = () => swiper?.activeIndex > 0 && swiper?.slidePrev();
	const nextSlide = () =>
		swiper?.activeIndex < shareableImages.length - 1 && swiper?.slideNext();

	if (shareableImages.length === 1)
		return (
			<WrappedSection sectionName="ParagraphShareableCarousel">
				<div className={styles.innercontainer}>
					<div className={styles.swiper}>
						{shareableImages[0] && (
							<div className={styles.imagewrapper}>
								<ProcessedImage
									src={shareableImages[0].imageSrc}
									dimensions={"640x360"}
									width={626}
									height={352}
									alt={shareableImages[0].imageAlt}
								/>
							</div>
						)}
					</div>
					<div className={styles.sharetools}>
						<img
							src={shareShape}
							width={423}
							height={288}
							alt=""
							loading={"lazy"}
							aria-hidden={true}
						/>
						<img
							src={shareDog}
							className={styles.sharedog}
							width={83}
							height={117}
							alt=""
							loading={"lazy"}
							aria-hidden={true}
						/>
						<SocialMediaIcons
							className={styles.sharetoicons}
							iconSize="40px"
							invert={true}
							xUrl={
								shareableImages[0].imagePageUrl
									? `https://twitter.com/intent/tweet?url=${shareableImages[0].imagePageUrl}&via=${process.env.GATSBY_IS_IE === "true" ? "DogsTrust_IE" : "DogsTrust"}`
									: ""
							}
							facebookUrl={
								shareableImages[0].imagePageUrl
									? `https://www.facebook.com/sharer/sharer.php?u=${shareableImages[0].imagePageUrl}`
									: ""
							}
							mailToUrl={
								shareableImages[0].imagePageUrl
									? `mailto:?subject=I saw this on the Dogs Trust’s website...&body=Check out this image: ${shareableImages[0].imagePageUrl}`
									: ""
							}
							whatsAppUrl={
								shareableImages[0].imagePageUrl
									? `https://wa.me/?text=${shareableImages[0].imagePageUrl}`
									: ""
							}
						/>
					</div>
				</div>
			</WrappedSection>
		);

	return (
		<WrappedSection sectionName="ParagraphShareableCarousel">
			<div className={styles.innercontainer}>
				{isClientSide && (
					<>
						<Swiper
							className={styles.swiper}
							key={shareableImages.length}
							modules={[Virtual, Navigation, A11y]}
							spaceBetween={0}
							slidesPerView={1.25}
							edgeSwipeThreshold={10}
							threshold={0}
							grabCursor={true}
							resistance={false}
							shortSwipes={touchDevice}
							loop={false}
							autoplay={false}
							effect="slide"
							onSwiper={(swiper) => setSwiper(swiper)}
							scrollbar={{ draggable: true }}
							onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
							initialSlide={0}
						>
							{shareableImages.map((image) => (
								<SwiperSlide key={image.imageSrc}>
									<div className={styles.imagewrapper}>
										<ProcessedImage
											src={image.imageSrc}
											dimensions={"640x360"}
											width={626}
											height={352}
											alt={image.imageAlt}
										/>
									</div>
								</SwiperSlide>
							))}
						</Swiper>
						<div className={styles.navbuttons}>
							<button
								type="button"
								aria-label="previous"
								className={styles.back}
								onClick={prevSlide}
								onKeyDown={(e) => {
									e.key === "Enter" && prevSlide();
								}}
							>
								{currentSlide > 0 && <ArrowCircle rotation={180} />}
							</button>
							<button
								type="button"
								aria-label="next"
								className={styles.next}
								onClick={nextSlide}
								onKeyDown={(e) => {
									e.key === "Enter" && nextSlide();
								}}
								disabled={currentSlide === shareableImages.length - 1}
							>
								{currentSlide < shareableImages.length - 1 && (
									<ArrowCircle rotation={0} />
								)}
								{currentSlide === shareableImages.length - 1 && (
									<ArrowCircle rotation={0} fill={Colours.DAWN} />
								)}
							</button>
						</div>
					</>
				)}
				<div className={styles.sharetools}>
					<img
						src={shareShape}
						width={423}
						height={288}
						alt=""
						loading={"lazy"}
						aria-hidden={true}
					/>
					<img
						src={shareDog}
						className={styles.sharedog}
						width={83}
						height={117}
						alt=""
						loading={"lazy"}
						aria-hidden={true}
					/>
					<SocialMediaIcons
						className={styles.sharetoicons}
						iconSize="40px"
						invert={true}
						xUrl={
							shareableImages[currentSlide].imagePageUrl
								? `https://twitter.com/intent/tweet?url=${shareableImages[currentSlide].imagePageUrl}&via=${process.env.GATSBY_IS_IE === "true" ? "DogsTrust_IE" : "DogsTrust"}`
								: ""
						}
						facebookUrl={
							shareableImages[currentSlide].imagePageUrl
								? `https://www.facebook.com/sharer/sharer.php?u=${shareableImages[currentSlide].imagePageUrl}`
								: ""
						}
						mailToUrl={
							shareableImages[currentSlide].imagePageUrl
								? `mailto:?subject=I saw this on the Dogs Trust’s website...&body=Check out this image: ${shareableImages[currentSlide].imagePageUrl}`
								: ""
						}
						whatsAppUrl={
							shareableImages[currentSlide].imagePageUrl
								? `https://wa.me/?text=${shareableImages[currentSlide].imagePageUrl}`
								: ""
						}
					/>
				</div>
			</div>
		</WrappedSection>
	);
};

export default ParagraphShareableCarousel;
